import { z } from 'zod';

export const MAX_PAYLOAD_SIZE = 4096;
export const MAX_ROOM_ID_LENGTH = 20;
export const MAX_USER_ID_LENGTH = 64;
export const MAX_NICKNAME_LENGTH = 24;
export const MAX_MESSAGE_LENGTH = 500;
export const MAX_POSITION = 86_400;
export const MAX_PARTICIPANTS = 20;

const finiteNumber = z.number().finite();
const positionSchema = finiteNumber.min(0).max(MAX_POSITION);
const revisionSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const userIdSchema = z.string().min(16).max(MAX_USER_ID_LENGTH).regex(/^[a-f0-9]+$/);
const roomIdSchema = z.string().min(12).max(MAX_ROOM_ID_LENGTH).regex(/^[A-Za-z0-9]+$/);
const nicknameSchema = z.string().trim().min(1).max(MAX_NICKNAME_LENGTH).regex(/^[^\x00-\x1F\x7F]+$/);
const videoIdSchema = z.string().length(11).regex(/^[A-Za-z0-9_-]+$/);
const mediaUrlSchema = z.string().url().max(2048).refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch {
    return false;
  }
}, 'Media URLs must use HTTPS and must not contain credentials');

export const VideoSourceSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('youtube'), videoId: videoIdSchema }).strict(),
  z.object({
    provider: z.literal('twitch'),
    kind: z.enum(['channel', 'vod', 'clip']),
    id: z.string().min(1).max(100).regex(/^[A-Za-z0-9_-]+$/),
  }).strict(),
  z.object({ provider: z.literal('direct'), mediaUrl: mediaUrlSchema }).strict(),
  z.object({ provider: z.literal('hls'), mediaUrl: mediaUrlSchema }).strict(),
  z.object({ provider: z.literal('iframe'), mediaUrl: mediaUrlSchema }).strict(),
]);

export type VideoSource = z.infer<typeof VideoSourceSchema>;

export const CanonicalVideoStateSchema = z.object({
  source: VideoSourceSchema,
  playing: z.boolean(),
  position: positionSchema,
  updatedAt: z.number().int().nonnegative(),
  revision: revisionSchema,
}).strict();

export type CanonicalVideoState = z.infer<typeof CanonicalVideoStateSchema>;

export const ParticipantSchema = z.object({
  userId: userIdSchema,
  nickname: z.string().min(1).max(MAX_NICKNAME_LENGTH),
  isHost: z.boolean(),
}).strict();

export type Participant = z.infer<typeof ParticipantSchema>;

export const ChatMessageSchema = z.object({
  id: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
  userId: userIdSchema,
  nickname: z.string().min(1).max(MAX_NICKNAME_LENGTH),
  message: z.string().min(1).max(MAX_MESSAGE_LENGTH),
  timestamp: z.number().int().nonnegative(),
}).strict();

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('JOIN'),
    roomId: roomIdSchema,
    nickname: nicknameSchema,
    password: z.string().max(100),
  }).strict(),
  z.object({
    type: z.literal('PLAY'),
    position: positionSchema,
    clientRevision: revisionSchema,
  }).strict(),
  z.object({
    type: z.literal('PAUSE'),
    position: positionSchema,
    clientRevision: revisionSchema,
  }).strict(),
  z.object({
    type: z.literal('SEEK'),
    position: positionSchema,
    clientRevision: revisionSchema,
  }).strict(),
  z.object({
    type: z.literal('VIDEO_CHANGE'),
    source: VideoSourceSchema,
    clientRevision: revisionSchema,
  }).strict(),
  z.object({ type: z.literal('ROTATE_PASSWORD') }).strict(),
  z.object({
    type: z.literal('CHAT_MESSAGE'),
    id: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
    message: z.string().trim().min(1).max(MAX_MESSAGE_LENGTH),
  }).strict(),
  z.object({ type: z.literal('PING') }).strict(),
]);

export type ClientMessage = z.infer<typeof ClientMessageSchema>;

const errorCodeSchema = z.enum([
  'INVALID_EVENT',
  'NOT_JOINED',
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'WRONG_PASSWORD',
  'PASSWORD_LOCKED',
  'PASSWORD_CHANGED',
  'INVALID_NICKNAME',
  'INVALID_VIDEO',
  'NOT_HOST',
  'RATE_LIMITED',
  'ROOM_CLOSED',
  'SERVER_ERROR',
]);

export type ErrorCode = z.infer<typeof errorCodeSchema>;

const playbackMessageSchema = z.object({
  type: z.union([z.literal('PLAY'), z.literal('PAUSE'), z.literal('SEEK')]),
  state: CanonicalVideoStateSchema,
  userId: userIdSchema,
}).strict();

export const ServerMessageSchema = z.union([
  z.object({
    type: z.literal('JOIN_SUCCESS'),
    userId: userIdSchema,
    hostId: userIdSchema,
  }).strict(),
  z.object({ type: z.literal('PASSWORD_ROTATED'), password: z.string().min(1).max(100) }).strict(),
  z.object({
    type: z.literal('ROOM_STATE'),
    state: CanonicalVideoStateSchema,
    participants: z.array(ParticipantSchema).max(MAX_PARTICIPANTS),
    chatHistory: z.array(ChatMessageSchema).max(50),
  }).strict(),
  playbackMessageSchema,
  z.object({
    type: z.literal('VIDEO_CHANGE'),
    state: CanonicalVideoStateSchema,
    userId: userIdSchema,
  }).strict(),
  z.object({
    type: z.literal('CHAT_MESSAGE'),
    id: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
    userId: userIdSchema,
    nickname: z.string().min(1).max(MAX_NICKNAME_LENGTH),
    message: z.string().min(1).max(MAX_MESSAGE_LENGTH),
    timestamp: z.number().int().nonnegative(),
  }).strict(),
  z.object({
    type: z.literal('USER_JOIN'),
    userId: userIdSchema,
    nickname: z.string().min(1).max(MAX_NICKNAME_LENGTH),
    participantCount: z.number().int().min(0).max(MAX_PARTICIPANTS),
  }).strict(),
  z.object({
    type: z.literal('USER_LEAVE'),
    userId: userIdSchema,
    nickname: z.string().min(1).max(MAX_NICKNAME_LENGTH),
    participantCount: z.number().int().min(0).max(MAX_PARTICIPANTS),
  }).strict(),
  z.object({
    type: z.literal('HOST_CHANGED'),
    newHostId: userIdSchema,
    newHostNickname: z.string().min(1).max(MAX_NICKNAME_LENGTH),
  }).strict(),
  z.object({
    type: z.literal('ERROR'),
    code: errorCodeSchema,
    message: z.string().max(200),
    retryAfterMs: z.number().int().nonnegative().optional(),
  }).strict(),
  z.object({ type: z.literal('PONG') }).strict(),
]);

export type ServerMessage = z.infer<typeof ServerMessageSchema>;

export function parseClientMessage(raw: string): ClientMessage | null {
  if (new TextEncoder().encode(raw).length > MAX_PAYLOAD_SIZE) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = ClientMessageSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function parseServerMessage(raw: string): ServerMessage | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = ServerMessageSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export interface CreateRoomRequest {
  source: VideoSource;
}

export const CreateRoomSuccessResponseSchema = z.object({
  success: z.literal(true),
  roomId: roomIdSchema,
  password: z.string().min(1).max(100),
}).strict();

export const CreateRoomErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(200),
  }).strict(),
}).strict();

export type CreateRoomResponse = z.infer<typeof CreateRoomSuccessResponseSchema>;
export type CreateRoomErrorResponse = z.infer<typeof CreateRoomErrorResponseSchema>;

export interface RoomExistsResponse {
  exists: boolean;
  participantCount: number;
}

export interface RoomStateModel {
  roomId: string;
  hostId: string;
  video: CanonicalVideoState;
  participants: Participant[];
}

export interface ConnectionStateModel {
  status: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
  retryCount: number;
  error: string | null;
}

export interface SecurityStateModel {
  requiresPassword: boolean;
  lastError: 'WRONG_PASSWORD' | 'PASSWORD_LOCKED' | 'ROOM_NOT_FOUND' | null;
}

export interface ChatStateModel {
  messages: ChatMessage[];
  unreadCount: number;
}
